import { SegmentResponse } from "@/graphql/types/graphql";
import { Segment, SegmentationMethod } from "../types/types";

export interface SegmentRange {
  range: string;
  segment: Segment | null;
  data?: SegmentResponse;
  segmentValue?: string;
  segmentIndex: number;
}

export interface TableSegment {
  index: number;
  segmentType: string;
  method: SegmentationMethod;
  ranges: SegmentRange[];
}

export interface CombinedRow {
  manualRange: string;
  dataValue: string;
  data?: SegmentResponse;
}

export const sortRows = (
  rows: SegmentRange[][],
  sortColumn: string,
  sortDirection: "asc" | "desc",
  isDataOnly: boolean
): SegmentRange[][] => {
  return rows
    .filter((row) => row.some((r) => r.data))
    .sort((a, b) => {
      const aData = a.find((r) => r.data)?.data;
      const bData = b.find((r) => r.data)?.data;
      if (sortColumn.startsWith("range") || sortColumn.startsWith("segmentValue")) {
        const segmentIndex = parseInt(sortColumn.match(/\d+/)?.[0] || "1") - 1;
        const aValue = a[segmentIndex]?.segmentValue || a[segmentIndex]?.range || "";
        const bValue = b[segmentIndex]?.segmentValue || b[segmentIndex]?.range || "";
        const isNumeric = (value: string) => !isNaN(parseFloat(value.split("-")[0]));
        if (isDataOnly && !isNumeric(aValue)) {
          return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        const parseRange = (range: string) => {
          const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
          return match ? parseFloat(match[1] || match[2]) : 0;
        };
        const aNum = parseRange(aValue);
        const bNum = parseRange(bValue);
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      } else if (sortColumn === "total") {
        return sortDirection === "asc"
          ? (Number(aData?.total) || 0) - (Number(bData?.total) || 0)
          : (Number(bData?.total) || 0) - (Number(aData?.total) || 0);
      } else if (sortColumn === "count1") {
        return sortDirection === "asc"
          ? (Number(aData?.count1) || 0) - (Number(bData?.count1) || 0)
          : (Number(bData?.count1) || 0) - (Number(aData?.count1) || 0);
      } else if (sortColumn === "count2") {
        return sortDirection === "asc"
          ? (Number(aData?.count2) || 0) - (Number(bData?.count2) || 0)
          : (Number(bData?.count2) || 0) - (Number(aData?.count2) || 0);
      } else if (sortColumn.startsWith("driver1")) {
        const metric = sortColumn.split(".")[1] as "min" | "percentile25" | "percentile50" | "percentile75" | "max";
        return sortDirection === "asc"
          ? (Number(aData?.driver1Metrics?.[metric]) || 0) - (Number(bData?.driver1Metrics?.[metric]) || 0)
          : (Number(bData?.driver1Metrics?.[metric]) || 0) - (Number(aData?.driver1Metrics?.[metric]) || 0);
      } else if (sortColumn.startsWith("driver2")) {
        const metric = sortColumn.split(".")[1] as "min" | "percentile25" | "percentile50" | "percentile75" | "max";
        return sortDirection === "asc"
          ? (Number(aData?.driver2Metrics?.[metric]) || 0) - (Number(bData?.driver2Metrics?.[metric]) || 0)
          : (Number(bData?.driver2Metrics?.[metric]) || 0) - (Number(aData?.driver2Metrics?.[metric]) || 0);
      }
      return 0;
    });
};

export const sortCombinedRows = (
  rows: CombinedRow[],
  sortColumn: string,
  sortDirection: "asc" | "desc"
): CombinedRow[] => {
  return rows
    .filter((row) => row.data)
    .sort((a, b) => {
      if (sortColumn === "range") {
        const parseRange = (range: string) => {
          const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
          return match ? parseFloat(match[1] || match[2]) : 0;
        };
        const aNum = parseRange(a.manualRange || a.dataValue);
        const bNum = parseRange(b.manualRange || b.dataValue);
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      } else if (sortColumn === "segmentValue") {
        const isNumeric = (value: string) => !isNaN(parseFloat(value.split("-")[0]));
        if (!isNumeric(a.dataValue)) {
          return sortDirection === "asc"
            ? a.dataValue.localeCompare(b.dataValue)
            : b.dataValue.localeCompare(a.dataValue);
        }
        const parseRange = (range: string) => {
          const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
          return match ? parseFloat(match[1] || match[2]) : 0;
        };
        const aNum = parseRange(a.dataValue);
        const bNum = parseRange(b.dataValue);
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      } else if (sortColumn === "total") {
        return sortDirection === "asc"
          ? (Number(a.data?.total) || 0) - (Number(b.data?.total) || 0)
          : (Number(b.data?.total) || 0) - (Number(a.data?.total) || 0);
      } else if (sortColumn === "count1") {
        return sortDirection === "asc"
          ? (Number(a.data?.count1) || 0) - (Number(b.data?.count1) || 0)
          : (Number(b.data?.count1) || 0) - (Number(a.data?.count1) || 0);
      } else if (sortColumn === "count2") {
        return sortDirection === "asc"
          ? (Number(a.data?.count2) || 0) - (Number(b.data?.count2) || 0)
          : (Number(b.data?.count2) || 0) - (Number(a.data?.count2) || 0);
      } else if (sortColumn.startsWith("driver1")) {
        const metric = sortColumn.split(".")[1] as "min" | "percentile25" | "percentile50" | "percentile75" | "max";
        return sortDirection === "asc"
          ? (Number(a.data?.driver1Metrics?.[metric]) || 0) - (Number(b.data?.driver1Metrics?.[metric]) || 0)
          : (Number(b.data?.driver1Metrics?.[metric]) || 0) - (Number(a.data?.driver1Metrics?.[metric]) || 0);
      } else if (sortColumn.startsWith("driver2")) {
        const metric = sortColumn.split(".")[1] as "min" | "percentile25" | "percentile50" | "percentile75" | "max";
        return sortDirection === "asc"
          ? (Number(a.data?.driver2Metrics?.[metric]) || 0) - (Number(b.data?.driver2Metrics?.[metric]) || 0)
          : (Number(b.data?.driver2Metrics?.[metric]) || 0) - (Number(a.data?.driver2Metrics?.[metric]) || 0);
      }
      return 0;
    });
};

export const formatCombinedTableData = (
  segments: TableSegment[],
  data: SegmentResponse[] | undefined
): { manualSegment: TableSegment | undefined; dataSegment: TableSegment | undefined; combinedRows: CombinedRow[] } => {
  const sortedSegments = [...segments].sort((a, b) => a.segmentType.localeCompare(b.segmentType));
  const manualSegment = sortedSegments.find((seg) => seg.method === "MANUAL");
  const dataSegment = sortedSegments.find((seg) => seg.method === "DATA");

  if (!data || data.length === 0 || !manualSegment || !dataSegment) {
    return { manualSegment, dataSegment, combinedRows: [] };
  }

  const primaryType = data[0]?.segment;
  const isManualPrimary = primaryType === manualSegment.segmentType;

  const seenCombinations = new Set<string>();
  const combinedRows = data
    .filter((d) => {
      const combinationKey = `${d.segmentValue}_${d.segmentRange || "null"}`;
      if (seenCombinations.has(combinationKey)) return false;
      seenCombinations.add(combinationKey);
      return true;
    })
    .map((d) => ({
      manualRange: isManualPrimary ? d.segmentValue : d.segmentRange || "",
      dataValue: isManualPrimary ? d.segmentRange || "" : d.segmentValue,
      data: d,
    }));

  return { manualSegment, dataSegment, combinedRows };
};

export const formatTableData = (
  segments: TableSegment[],
  data: SegmentResponse[] | undefined
): { displaySegments: TableSegment[]; rows: SegmentRange[][]; isDataOnly: boolean } => {
  const displaySegments = [...segments].sort((a, b) => a.segmentType.localeCompare(b.segmentType));
  const isDataOnly = displaySegments.every((seg) => seg.method === "DATA");
  const isManualOnly = displaySegments.every((seg) => seg.method === "MANUAL") && segments.length === 1;
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isTwoSameMethod) {
    const [firstSegment, secondSegment] = displaySegments;
    const rows: SegmentRange[][] = [];
    const seenCombinations = new Set<string>();
    firstSegment.ranges.forEach((firstRange) => {
      secondSegment.ranges.forEach((secondRange) => {
        const combinationKey = `${firstRange.segmentValue || firstRange.range}_${
          secondRange.segmentValue || secondRange.range
        }`;
        if (!seenCombinations.has(combinationKey)) {
          seenCombinations.add(combinationKey);
          const matchingData = data?.find(
            (d) =>
              d.segment === firstSegment.segmentType &&
              d.segmentValue === (firstRange.segmentValue || firstRange.range) &&
              d.segmentRange === (secondRange.segmentValue || secondRange.range)
          );
          rows.push([
            {
              range: firstRange.segmentValue || firstRange.range,
              segment: firstRange.segment,
              segmentValue: firstRange.segmentValue || firstRange.range,
              data: matchingData,
              segmentIndex: firstSegment.index,
            },
            {
              range: secondRange.segmentValue || secondRange.range,
              segment: secondRange.segment,
              segmentValue: secondRange.segmentValue || secondRange.range,
              data: undefined,
              segmentIndex: secondSegment.index,
            },
          ]);
        }
      });
    });
    return { displaySegments, rows, isDataOnly };
  }

  const rows: SegmentRange[][] = [];
  const seenValues = new Set<string>();
  const validData = data || [];
  displaySegments[0]?.ranges?.forEach((range) => {
    const value = range.segmentValue || range.range || "";
    if (!seenValues.has(value)) {
      seenValues.add(value);
      const matchingData = validData.find(
        (d) => d.segment === displaySegments[0].segmentType && d.segmentValue === value
      );
      if (matchingData || isManualOnly) {
        rows.push([
          {
            range: range.range || "",
            segment: range.segment,
            segmentValue: range.segmentValue || range.range || "",
            data: matchingData,
            segmentIndex: displaySegments[0].index,
          },
        ]);
      }
    }
  });

  return { displaySegments, rows, isDataOnly };
};
